import { memo, useCallback } from 'react'
import { Loader2, X } from 'lucide-react'
import { toast } from 'sonner'
import { useSubscription } from '@/hooks/useSubscription'
import { useRenewSubscription } from '@/hooks/useRenewSubscription'
import { useUsdcBalance } from '@/hooks/useUsdcBalance'
import { Button } from '@/components/ui/button'

const SUBSCRIPTION_USDC = 8.5
const SUBSCRIPTION_DAYS = 30

interface Props {
  open: boolean
  onClose: () => void
}

function formatDate(ts: number): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function daysLeft(ts: number): number {
  const now = Math.floor(Date.now() / 1000)
  return Math.max(0, Math.ceil((ts - now) / 86400))
}

export const PlanDialog = memo(function PlanDialog({ open, onClose }: Props) {
  const { address, exists, isActive, onTrial, trialEndsAt, paidUntil } = useSubscription()
  const { balance } = useUsdcBalance()
  const renew = useRenewSubscription()

  const effectiveUntil = Math.max(paidUntil, trialEndsAt)
  const insufficientBalance = balance != null && balance < SUBSCRIPTION_USDC
  const canRenew = !!address && exists && !renew.isPending && !insufficientBalance

  const handleRenew = useCallback(async () => {
    try {
      await renew.mutateAsync()
      toast.success('Оплата отправлена. Активация в течение 10–30 с.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка оплаты'
      toast.error(msg)
    }
  }, [renew])

  if (!open) return null

  let statusLine: string
  if (!address) {
    statusLine = 'Подключите PM-кошелёк — триал 7 дней активируется автоматически.'
  } else if (!exists) {
    statusLine = 'Триал активируется при подключении кошелька.'
  } else if (onTrial) {
    statusLine = `Trial · осталось ${daysLeft(trialEndsAt)} дн (до ${formatDate(trialEndsAt)})`
  } else if (isActive) {
    statusLine = `Активна до ${formatDate(paidUntil)} (${daysLeft(paidUntil)} дн)`
  } else {
    statusLine = 'Подписка истекла'
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-[#374151] bg-[#111827] p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 id="plan-title" className="text-lg font-semibold text-white">Подписка</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-white/10 hover:text-white"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-[#374151] bg-[#1f2937] px-4 py-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-[#22c55e]">PolyStrike Visual</span>
            <span className="text-sm text-white tabular-nums">
              {SUBSCRIPTION_USDC} USDC / {SUBSCRIPTION_DAYS} дней
            </span>
          </div>
          <div className="mt-2 text-xs text-[#e2e8f0]">{statusLine}</div>
          {balance != null && (
            <div className="mt-2 text-[11px]">
              <span className="text-[#94a3b8]">Баланс USDC: </span>
              <span className={insufficientBalance ? 'text-[#ef4444]' : 'text-white'}>
                {balance.toFixed(2)}
              </span>
              {insufficientBalance && (
                <span className="text-[#ef4444]"> · недостаточно</span>
              )}
            </div>
          )}
        </div>

        <Button
          onClick={() => void handleRenew()}
          disabled={!canRenew}
          className="w-full bg-[#22c55e] text-black hover:bg-[#16a34a]"
        >
          {renew.isPending ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Оплата…</>
          ) : (
            `Оплатить ${SUBSCRIPTION_USDC} USDC · +${SUBSCRIPTION_DAYS} дней`
          )}
        </Button>

        <div className="mt-3 text-[11px] leading-relaxed text-[#64748b]">
          Оплатить можно в любой момент — 30 дней добавляются поверх текущего
          доступа (триала или уже оплаченного периода), дни не сгорают.
          Первая активация — 7-дневный trial автоматически при подключении
          PM-кошелька. {effectiveUntil > 0 && address ? (
            <>Действует до <span className="text-[#94a3b8]">{formatDate(effectiveUntil)}</span>.</>
          ) : null}
        </div>
      </div>
    </div>
  )
})
